import { $commitActiveComputerCommandQuery } from '#pplx/frontend/components/form/lexical/plugins/ComputerCommandQueryPluginSupport.js';
import { $getActiveComputerCommandQuery, $getCommandMenuQueryNode, $removeCommandMenuQueryNodes, } from '#pplx/frontend/components/form/lexical/plugins/ComputerCommandQueryPluginSupport.js';
import { $createCommandMenuQueryNode } from '#pplx/frontend/composer-sdk/lexical/nodes/CommandMenuQueryNodeSupport.js';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createParagraphNode, $createTextNode, $getRoot, $getSelection, $isElementNode, $isRangeSelection, $isRootNode, $isTextNode, COMMAND_PRIORITY_HIGH, CONTROLLED_TEXT_INSERTION_COMMAND, createCommand, DELETE_CHARACTER_COMMAND, FOCUS_COMMAND, KEY_BACKSPACE_COMMAND, } from 'lexical';
import { useCallback, useEffect, useRef } from 'react';
export const COMPUTER_COMMAND_QUERY_UPDATE_TAG = 'computer-command-query-update';
export const INSERT_COMPUTER_COMMAND_QUERY_COMMAND = createCommand('INSERT_COMPUTER_COMMAND_QUERY_COMMAND');
export const COMMIT_COMPUTER_COMMAND_QUERY_COMMAND = createCommand('COMMIT_COMPUTER_COMMAND_QUERY_COMMAND');
function statesAreEqual(a, b) {
    return a?.query === b?.query && a?.source === b?.source;
}
function slashQueryStartsWithWhitespace(query, source) {
    return source === 'slash' && query.length > 0 && /\s/.test(query[0] ?? '');
}
function $shouldShowCommandQueryPlaceholder(query, source) {
    return query.length === 0 && (source === 'slash' || $getRoot().getTextContentSize() === 0);
}
function isSlashCommandStartBoundary(character) {
    return character === undefined || /\s/.test(character);
}
function isSlashCommandEndBoundary({ nextCharacter, textBeforeSlash, }) {
    if (nextCharacter === undefined)
        return true;
    return /\s/.test(nextCharacter) && textBeforeSlash.trim().length > 0;
}
function getTextFromInsertionPayload(payload) {
    return typeof payload === 'string' ? payload : (payload.data ?? undefined);
}
function $isSelectionAtActiveQueryEnd(activeQuery) {
    const selection = $getSelection();
    if (!$isRangeSelection(selection) || !selection.isCollapsed())
        return false;
    if (!activeQuery.textNode) {
        return (selection.anchor.getNode().getKey() === activeQuery.parentNode.getKey() &&
            selection.anchor.offset === activeQuery.markerOffset);
    }
    return (selection.anchor.getNode().getKey() === activeQuery.textNode.getKey() &&
        selection.anchor.offset === activeQuery.textNode.getTextContentSize());
}
function $hasExpandedRangeSelection() {
    const selection = $getSelection();
    return $isRangeSelection(selection) && !selection.isCollapsed();
}
function $getSlashComputerCommandAtCaret() {
    const selection = $getSelection();
    if (!$isRangeSelection(selection) || !selection.isCollapsed())
        return null;
    const anchorNode = selection.anchor.getNode();
    if (!$isTextNode(anchorNode))
        return null;
    const caretOffset = selection.anchor.offset;
    if (caretOffset <= 0)
        return null;
    const text = anchorNode.getTextContent();
    const slashOffset = text.lastIndexOf('/', caretOffset - 1);
    if (slashOffset < 0 || !isSlashCommandStartBoundary(text[slashOffset - 1]))
        return null;
    if (/\s/.test(text.slice(slashOffset + 1, caretOffset)))
        return null;
    if (!isSlashCommandEndBoundary({
        nextCharacter: text[caretOffset],
        textBeforeSlash: text.slice(0, slashOffset),
    })) {
        return null;
    }
    return {
        caretOffset,
        queryStartOffset: slashOffset + 1,
        textNode: anchorNode,
    };
}
function $collapseSelectionToFocus(selection) {
    if (selection.isCollapsed())
        return true;
    const focusNode = selection.focus.getNode();
    if (!$isTextNode(focusNode) && !$isElementNode(focusNode))
        return false;
    focusNode.select(selection.focus.offset, selection.focus.offset);
    return true;
}
function $getInsertionSelection() {
    const selection = $getSelection();
    if ($isRangeSelection(selection))
        return selection;
    $getRoot().selectEnd();
    const fallbackSelection = $getSelection();
    return $isRangeSelection(fallbackSelection) ? fallbackSelection : null;
}
function $insertCommandQueryMarkerAfterTextNode({ placeholderText, queryStartOffset, source, textNode, }) {
    textNode.insertAfter($createCommandMenuQueryNode({
        placeholderText,
        queryStartOffset,
        showPlaceholder: $shouldShowCommandQueryPlaceholder('', source),
        source,
    }));
    textNode.selectEnd();
}
function $insertCommandMenuQueryNodeAtSelection({ placeholderText, source, }) {
    const existingNode = $getCommandMenuQueryNode();
    if (existingNode)
        return true;
    const selection = $getInsertionSelection();
    if (!selection)
        return false;
    if (!$collapseSelectionToFocus(selection))
        return false;
    const collapsedSelection = $getSelection();
    if (!$isRangeSelection(collapsedSelection))
        return false;
    const anchorNode = collapsedSelection.anchor.getNode();
    if ($isTextNode(anchorNode)) {
        const insertionOffset = collapsedSelection.anchor.offset;
        let textNodeBeforeQuery = anchorNode;
        if (insertionOffset === 0) {
            textNodeBeforeQuery = $createTextNode('');
            anchorNode.insertBefore(textNodeBeforeQuery);
        }
        else if (insertionOffset < anchorNode.getTextContentSize()) {
            const splitNode = anchorNode.splitText(insertionOffset)[0];
            if (!splitNode)
                return false;
            textNodeBeforeQuery = splitNode;
        }
        $insertCommandQueryMarkerAfterTextNode({
            placeholderText,
            queryStartOffset: insertionOffset,
            source,
            textNode: textNodeBeforeQuery,
        });
        return true;
    }
    if (!$isElementNode(anchorNode))
        return false;
    const insertionOffset = collapsedSelection.anchor.offset;
    const textNodeBeforeQuery = $createTextNode('');
    if ($isRootNode(anchorNode)) {
        const paragraphNode = $createParagraphNode();
        const childAtOffset = anchorNode.getChildAtIndex(insertionOffset);
        if (childAtOffset) {
            childAtOffset.insertBefore(paragraphNode);
        }
        else {
            anchorNode.append(paragraphNode);
        }
        paragraphNode.append(textNodeBeforeQuery);
        $insertCommandQueryMarkerAfterTextNode({
            placeholderText,
            queryStartOffset: 0,
            source,
            textNode: textNodeBeforeQuery,
        });
        return true;
    }
    const childAtOffset = anchorNode.getChildAtIndex(insertionOffset);
    if (childAtOffset) {
        childAtOffset.insertBefore(textNodeBeforeQuery);
    }
    else {
        anchorNode.append(textNodeBeforeQuery);
    }
    $insertCommandQueryMarkerAfterTextNode({
        placeholderText,
        queryStartOffset: 0,
        source,
        textNode: textNodeBeforeQuery,
    });
    return true;
}
function $insertSlashCommandMenuQueryNodeAtSelection(placeholderText) {
    if ($getCommandMenuQueryNode())
        return true;
    const slashCommand = $getSlashComputerCommandAtCaret();
    if (!slashCommand)
        return false;
    const { caretOffset, queryStartOffset } = slashCommand;
    let { textNode } = slashCommand;
    if (caretOffset < textNode.getTextContentSize()) {
        const splitNode = textNode.splitText(caretOffset)[0];
        if (!splitNode)
            return false;
        textNode = splitNode;
    }
    $insertCommandQueryMarkerAfterTextNode({
        placeholderText,
        queryStartOffset,
        source: 'slash',
        textNode,
    });
    return true;
}
function $insertSlashComputerCommandQuery(placeholderText, text) {
    const selection = $getSelection();
    if (!$isRangeSelection(selection))
        return false;
    const hadExistingQueryNode = $getCommandMenuQueryNode() !== null;
    selection.insertText(text);
    if (hadExistingQueryNode)
        return true;
    $insertSlashCommandMenuQueryNodeAtSelection(placeholderText);
    return true;
}
function $deleteEmptyActiveComputerCommandQuery() {
    const activeQuery = $getActiveComputerCommandQuery();
    if (!activeQuery || activeQuery.query.length > 0 || !$isSelectionAtActiveQueryEnd(activeQuery)) {
        return false;
    }
    const { node, source, textNode } = activeQuery;
    if (source === 'slash') {
        if (!textNode)
            return false;
        const text = textNode.getTextContent();
        const slashOffset = node.__queryStartOffset - 1;
        if (slashOffset < 0 || text[slashOffset] !== '/')
            return false;
        textNode.setTextContent(text.slice(0, slashOffset) + text.slice(node.__queryStartOffset));
        node.remove();
        textNode.select(slashOffset, slashOffset);
        return true;
    }
    node.remove();
    if (textNode) {
        textNode.select(node.__queryStartOffset, node.__queryStartOffset);
    }
    else {
        activeQuery.parentNode.select(activeQuery.markerOffset, activeQuery.markerOffset);
    }
    return true;
}
export function ComputerCommandQueryPlugin({ enabled, onQueryChange, placeholderText, }) {
    const [editor] = useLexicalComposerContext();
    const previousQueryRef = useRef(undefined);
    // Closing intentionally leaves `/command` text in the editor. Suppress the update caused by
    // marker removal, then allow focus or the next insertion to rebuild the query from that text.
    const suppressSlashQueryRecoveryRef = useRef(false);
    const notifyQueryChange = useCallback((queryState) => {
        if (statesAreEqual(previousQueryRef.current, queryState))
            return;
        previousQueryRef.current = queryState;
        onQueryChange?.(queryState);
    }, [onQueryChange]);
    useEffect(() => {
        if (enabled)
            return;
        notifyQueryChange(undefined);
        editor.update(() => {
            $removeCommandMenuQueryNodes();
        }, { tag: COMPUTER_COMMAND_QUERY_UPDATE_TAG });
    }, [editor, enabled, notifyQueryChange]);
    useEffect(() => {
        const textInsertionCommand = editor.registerCommand(CONTROLLED_TEXT_INSERTION_COMMAND, (payload) => {
            const text = getTextFromInsertionPayload(payload);
            if (!enabled || text === undefined)
                return false;
            if (!text.startsWith('/')) {
                suppressSlashQueryRecoveryRef.current = false;
                $insertSlashCommandMenuQueryNodeAtSelection(placeholderText);
                return false;
            }
            suppressSlashQueryRecoveryRef.current = false;
            return $insertSlashComputerCommandQuery(placeholderText, text);
        }, COMMAND_PRIORITY_HIGH);
        const insertCommandQueryCommand = editor.registerCommand(INSERT_COMPUTER_COMMAND_QUERY_COMMAND, () => {
            if (!enabled)
                return false;
            return $insertCommandMenuQueryNodeAtSelection({
                placeholderText,
                source: 'manual',
            });
        }, COMMAND_PRIORITY_HIGH);
        const commitCommandQueryCommand = editor.registerCommand(COMMIT_COMPUTER_COMMAND_QUERY_COMMAND, () => {
            const didCommit = $commitActiveComputerCommandQuery();
            suppressSlashQueryRecoveryRef.current = didCommit;
            return didCommit;
        }, COMMAND_PRIORITY_HIGH);
        const focusCommand = editor.registerCommand(FOCUS_COMMAND, () => {
            suppressSlashQueryRecoveryRef.current = false;
            if (enabled) {
                $insertSlashCommandMenuQueryNodeAtSelection(placeholderText);
            }
            return false;
        }, COMMAND_PRIORITY_HIGH);
        const backspaceCommand = editor.registerCommand(KEY_BACKSPACE_COMMAND, (event) => {
            if (!enabled || !$deleteEmptyActiveComputerCommandQuery())
                return false;
            event.preventDefault();
            event.stopPropagation();
            return true;
        }, COMMAND_PRIORITY_HIGH);
        const deleteCharacterCommand = editor.registerCommand(DELETE_CHARACTER_COMMAND, (isBackward) => {
            if (!enabled || !isBackward)
                return false;
            return $deleteEmptyActiveComputerCommandQuery();
        }, COMMAND_PRIORITY_HIGH);
        return () => {
            textInsertionCommand();
            insertCommandQueryCommand();
            commitCommandQueryCommand();
            focusCommand();
            backspaceCommand();
            deleteCharacterCommand();
        };
    }, [editor, enabled, placeholderText]);
    useEffect(() => {
        return editor.registerUpdateListener(({ editorState, tags }) => {
            if (tags.has(COMPUTER_COMMAND_QUERY_UPDATE_TAG))
                return;
            let nextQueryState;
            let shouldInsertSlashQueryNode = false;
            let shouldRemoveQueryNode = false;
            let shouldShowPlaceholder;
            editorState.read(() => {
                const queryNode = $getCommandMenuQueryNode();
                if (!queryNode) {
                    shouldInsertSlashQueryNode =
                        enabled &&
                            !suppressSlashQueryRecoveryRef.current &&
                            $getSlashComputerCommandAtCaret() !== null;
                    return;
                }
                if (!enabled) {
                    shouldRemoveQueryNode = true;
                    return;
                }
                const activeQuery = $getActiveComputerCommandQuery();
                if (!activeQuery ||
                    (!$isSelectionAtActiveQueryEnd(activeQuery) && !$hasExpandedRangeSelection())) {
                    shouldRemoveQueryNode = true;
                    return;
                }
                if (slashQueryStartsWithWhitespace(activeQuery.query, activeQuery.source)) {
                    shouldRemoveQueryNode = true;
                    return;
                }
                nextQueryState = {
                    query: activeQuery.query,
                    source: activeQuery.source,
                };
                shouldShowPlaceholder = $shouldShowCommandQueryPlaceholder(activeQuery.query, activeQuery.source);
            });
            notifyQueryChange(nextQueryState);
            if (shouldInsertSlashQueryNode) {
                editor.update(() => {
                    $insertSlashCommandMenuQueryNodeAtSelection(placeholderText);
                });
                return;
            }
            if (!shouldRemoveQueryNode && shouldShowPlaceholder === undefined)
                return;
            editor.update(() => {
                if (shouldRemoveQueryNode) {
                    $removeCommandMenuQueryNodes();
                    return;
                }
                const activeQuery = $getActiveComputerCommandQuery();
                if (activeQuery && activeQuery.node.__showPlaceholder !== shouldShowPlaceholder) {
                    activeQuery.node.setShowPlaceholder(Boolean(shouldShowPlaceholder));
                }
            }, { tag: COMPUTER_COMMAND_QUERY_UPDATE_TAG });
        });
    }, [editor, enabled, notifyQueryChange, placeholderText]);
    return null;
}
//# sourceMappingURL=ComputerCommandQueryPlugin.js.map